// frontend/src/modules/folha/pages/AdiantamentoForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaSave, FaTimes, FaUser, FaCalendar, FaMoneyBillWave } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || '';

function AdiantamentoForm({ usuario, isDark = false }) {
  const navigate = useNavigate();
  const { id } = useParams(); // Para edição
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    funcionario_id: '',
    mes_referencia: '',
    ano_referencia: new Date().getFullYear(),
    valor_solicitado: '',
    data_solicitacao: new Date().toISOString().split('T')[0],
    observacoes: ''
  });

  const token = localStorage.getItem('token');
  const empresaId = usuario?.empresa_id || 1;

  const meses = [
    { valor: 'janeiro', label: 'Janeiro' },
    { valor: 'fevereiro', label: 'Fevereiro' },
    { valor: 'março', label: 'Março' },
    { valor: 'abril', label: 'Abril' },
    { valor: 'maio', label: 'Maio' },
    { valor: 'junho', label: 'Junho' },
    { valor: 'julho', label: 'Julho' },
    { valor: 'agosto', label: 'Agosto' },
    { valor: 'setembro', label: 'Setembro' },
    { valor: 'outubro', label: 'Outubro' },
    { valor: 'novembro', label: 'Novembro' },
    { valor: 'dezembro', label: 'Dezembro' }
  ];

  // ============================================
  // BUSCAR FUNCIONÁRIOS
  // ============================================
  useEffect(() => {
    const fetchFuncionarios = async () => {
      try {
        const response = await fetch(`${API_URL}/api/folha/empresa/${empresaId}/funcionarios`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Erro ao buscar funcionários');
        const data = await response.json();
        setFuncionarios(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Erro:', err);
        setError('Erro ao carregar funcionários');
      }
    };

    fetchFuncionarios();
  }, [empresaId, token]);

  // ============================================
  // SE FOR EDIÇÃO, BUSCAR DADOS
  // ============================================
  useEffect(() => {
    if (!id || id === 'novo') return;

    const fetchAdiantamento = async () => {
      try {
        const response = await fetch(`${API_URL}/api/adiantamento/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Erro ao buscar adiantamento');
        const data = await response.json();
        setFormData({
          funcionario_id: data.funcionario_id,
          mes_referencia: data.mes_referencia,
          ano_referencia: data.ano_referencia,
          valor_solicitado: data.valor_solicitado,
          data_solicitacao: data.data_solicitacao?.split('T')[0] || '',
          observacoes: data.observacoes || ''
        });
      } catch (err) {
        setError(err.message);
      }
    };

    fetchAdiantamento();
  }, [id, token]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const url = id && id !== 'novo'
        ? `${API_URL}/api/adiantamento/${id}`
        : `${API_URL}/api/folha/funcionarios/${formData.funcionario_id}/adiantamentos`;

      const method = id && id !== 'novo' ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          valor_solicitado: parseFloat(formData.valor_solicitado) || 0
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao salvar adiantamento');
      }

      setSuccess('✅ Adiantamento salvo com sucesso!');
      
      setTimeout(() => {
        navigate('/adiantamento');
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // ESTILOS
  // ============================================
  const colors = {
    background: isDark ? '#121212' : '#f8f9fa',
    card: isDark ? '#1e1e1e' : '#ffffff',
    text: isDark ? '#e9ecef' : '#212529',
    textSecondary: isDark ? '#adb5bd' : '#6c757d',
    border: isDark ? '#333333' : '#dee2e6',
    inputBg: isDark ? '#2d2d2d' : '#ffffff'
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    fontSize: '14px',
    backgroundColor: colors.inputBg,
    color: colors.text,
    marginTop: '5px',
    boxSizing: 'border-box'
  };

  return (
    <div style={{
      padding: '20px',
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: colors.background,
      minHeight: '100vh'
    }}>
      {/* Cabeçalho */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => navigate('/adiantamento')}
          style={{
            background: 'none',
            border: 'none',
            color: colors.textSecondary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '15px',
            fontSize: '14px'
          }}
        >
          ← Voltar
        </button>
        <h2 style={{ color: colors.text, margin: 0 }}>
          <FaMoneyBillWave /> {id && id !== 'novo' ? 'Editar Adiantamento' : 'Novo Adiantamento'}
        </h2>
      </div>

      {/* Mensagens */}
      {error && (
        <div style={{
          backgroundColor: isDark ? '#3a1a1a' : '#ffebee',
          padding: '12px',
          borderRadius: '6px',
          color: isDark ? '#e8b8b8' : '#c62828',
          marginBottom: '15px'
        }}>
          ❌ {error}
        </div>
      )}
      {success && (
        <div style={{
          backgroundColor: isDark ? '#1a3a1a' : '#e8f5e9',
          padding: '12px',
          borderRadius: '6px',
          color: isDark ? '#a8d8a8' : '#2e7d32',
          marginBottom: '15px'
        }}>
          {success}
        </div>
      )}

      {/* Formulário */}
      <form onSubmit={handleSubmit} style={{
        backgroundColor: colors.card,
        padding: '25px',
        borderRadius: '8px',
        border: isDark ? `1px solid ${colors.border}` : 'none',
        boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        
        {/* Funcionário */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ color: colors.text, fontWeight: '500', fontSize: '14px' }}>
            <FaUser /> Funcionário *
          </label>
          <select
            name="funcionario_id"
            value={formData.funcionario_id}
            onChange={handleChange}
            required
            style={inputStyle}
          >
            <option value="">Selecione...</option>
            {funcionarios.map(f => (
              <option key={f.id} value={f.id}>
                {f.nome_completo} - CPF: {f.cpf}
              </option>
            ))}
          </select>
        </div>

        {/* Mês e Ano */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
          <div>
            <label style={{ color: colors.text, fontWeight: '500', fontSize: '14px' }}>
              <FaCalendar /> Mês de Referência *
            </label>
            <select
              name="mes_referencia"
              value={formData.mes_referencia}
              onChange={handleChange}
              required
              style={inputStyle}
            >
              <option value="">Selecione...</option>
              {meses.map(m => (
                <option key={m.valor} value={m.valor}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ color: colors.text, fontWeight: '500', fontSize: '14px' }}>
              Ano *
            </label>
            <select
              name="ano_referencia"
              value={formData.ano_referencia}
              onChange={handleChange}
              required
              style={inputStyle}
            >
              {[2024, 2025, 2026, 2027].map(ano => (
                <option key={ano} value={ano}>{ano}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Valor */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ color: colors.text, fontWeight: '500', fontSize: '14px' }}>
            💰 Valor Solicitado (R$) *
          </label>
          <input
            type="number"
            name="valor_solicitado"
            value={formData.valor_solicitado}
            onChange={handleChange}
            required
            step="0.01"
            min="0"
            placeholder="0,00"
            style={inputStyle}
          />
        </div>

        {/* Data da Solicitação */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ color: colors.text, fontWeight: '500', fontSize: '14px' }}>
            📅 Data da Solicitação *
          </label>
          <input
            type="date"
            name="data_solicitacao"
            value={formData.data_solicitacao}
            onChange={handleChange}
            required
            style={inputStyle}
          />
        </div>

        {/* Observações */}
        <div style={{ marginBottom: '25px' }}>
          <label style={{ color: colors.text, fontWeight: '500', fontSize: '14px' }}>
            📝 Observações
          </label>
          <textarea
            name="observacoes"
            value={formData.observacoes}
            onChange={handleChange}
            rows="3"
            placeholder="Observações adicionais..."
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        {/* Botões */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              flex: 1,
              minWidth: '150px',
              padding: '12px 20px',
              backgroundColor: loading ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '15px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <FaSave /> {loading ? 'Salvando...' : 'Salvar Adiantamento'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/adiantamento')}
            style={{
              flex: 1,
              minWidth: '150px',
              padding: '12px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <FaTimes /> Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdiantamentoForm;